import React, { useContext } from 'react';
import styled, { css, ThemeContext } from 'styled-components';

import ErrorContext from '../Controller/errorContext';
import Controller from '../Controller/Controller';
import CoreCell from '../../NewTable/_common/CoreCell';

import { NewRadioProps } from '../../NewRadio/NewRadio';
import useInkAnalytics from '../../../hooks/useInkAnalytics';

const StyledWrapper = styled(Controller.Wrapper)`
  ${CoreCell} > && {
    /*
     * Width for Radios inside NewTable
     * Compensates wrapper right paddings so the column width is always
     * correct even if just one cell or header has the "radio" preset
     */
    width: 0;
  }
`;

const RadioEntry = styled.input`
  ${Controller.styles.hideInput};

  &:checked + label > i {
    opacity: 1;
    transform: scale(1);
  }
`;

const RadioAppearance = styled.label`
  ${Controller.styles.base};
  ${Controller.styles.appearanceEntry};
  align-items: center;
  border-radius: 50%;
  height: 20px;
  min-width: 20px;
  width: 20px;
`;

const RadioIcon = styled.i`
  background-color: currentColor;
  border-radius: 50%;
  opacity: 0;
  height: 10px;
  width: 10px;
  transform: scale(0);
  transition: 100ms ease all;

  ${props =>
    props.theme.checked &&
    css`
      opacity: 1;
      transform: scale(1);
    `}
`;

const RadioLabel = styled.label`
  ${Controller.styles.appearanceLabel};
`;

function CoreRadio(props: NewRadioProps) {
  const { externalRef, ...otherProps } = props;
  const [ref] = useInkAnalytics(props, { ref: externalRef });
  const themeContext = useContext(ThemeContext);
  const errorContext = useContext(ErrorContext);

  return (
    <Controller
      {...otherProps}
      type="radio"
      aria-errormessage={
        errorContext.hasError && errorContext.errorAutoInvalidate ? errorContext.errorMessageId : undefined
      }
      aria-invalid={props.invalid || (errorContext.hasError && errorContext.errorAutoInvalidate)}
      render={controllerProps => {
        return (
          <StyledWrapper className={props.className} theme={{ ...themeContext, ...controllerProps.theme }}>
            <Controller.AppearanceWrapper theme={{ ...themeContext, ...controllerProps.theme }}>
              <RadioEntry {...controllerProps} ref={ref} />
              <RadioAppearance
                htmlFor={props.id}
                theme={{ ...themeContext, ...controllerProps.theme }}
                data-testid="radio-icon-label"
              >
                <RadioIcon theme={{ ...themeContext, checked: controllerProps.checked }} data-testid="radio-icon" />
              </RadioAppearance>
              {props.label && <RadioLabel htmlFor={props.id}>{props.label}</RadioLabel>}
            </Controller.AppearanceWrapper>
          </StyledWrapper>
        );
      }}
    />
  );
}

CoreRadio.defaultProps = {
  disabled: false,
  invalid: false,
  label: undefined,
  checked: undefined,
  onChange: undefined,
  width: 'large',
};

export default CoreRadio;
